"use client";

import Link from "next/link";
import { ArrowUpRight, ArrowDownRight, Users } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { sourceUrl, type Source } from "@/lib/source-url";
import { formatCents } from '@/lib/format-cents';

export interface FrenActivity {
  id: string;
  event_type: string;
  source_username?: string;
  source_wallet?: string;
  source_user_id?: string;
  market_slug?: string;
  market_title?: string;
  market_image?: string;
  outcome?: string;
  side?: string;
  size_usd?: number;
  price?: number;
  event_at: string;
  metadata: Record<string, any>;
}

interface FrensTradingGridProps {
  activity: FrenActivity[];
  newsSidebarOpen: boolean;
  source: Source;
}

/**
 * "Your Frens Are Trading" activity grid for the For-You category.
 *
 * Each card shows: avatar + display name + activity type badge (Fren /
 * Copy / Tracked) + buy/sell direction with outcome + amount + price +
 * relative timestamp + nested market card.
 *
 * Originally a 130-line block inline in MarketsPageContent. Three props
 * cover everything because all per-item derivations (timeAgo, badges,
 * formatted size) are computed from the activity row itself.
 */
export function FrensTradingGrid({ activity, newsSidebarOpen, source }: FrensTradingGridProps) {
  const t = useT();

  // Deduplicate: max 1 event per (user + market) combo, keep most recent
  const seen = new Set<string>();
  const dedupedActivity = activity.filter((act) => {
    const key = `${act.source_user_id || act.source_wallet}::${act.market_slug}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (dedupedActivity.length === 0) return null;

  return (
    <div className="mt-8 mb-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-monument font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-accent-primary" />
          {t("forYou.frensTrading")}
        </h3>
        <Link
          href="/activity?filter=following"
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-satoshi transition-colors"
        >
          {t("forYou.viewAll")}
        </Link>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${!newsSidebarOpen ? "xl:grid-cols-3 2xl:grid-cols-4" : "xl:grid-cols-2 2xl:grid-cols-3"} gap-3`}>
        {dedupedActivity.slice(0, 8).map((act) => {
          const displayName =
            act.metadata?.display_name ||
            act.source_username ||
            (act.source_wallet ? `${act.source_wallet.slice(0, 6)}...${act.source_wallet.slice(-4)}` : "Trader");
          const avatar = act.metadata?.user_image || act.metadata?.wallet_profile_image;
          const isBuy = act.side?.toUpperCase() === "BUY";
          // Some activity rows arrive with literal "Unknown" / "Unknown Market"
          // strings from the backend (older trade data missing fields). Swap
          // those for localised labels so the section reads naturally in ES.
          const rawOutcome = act.outcome || (isBuy ? "YES" : "NO");
          const outcomeLabel = /^unknown$/i.test(rawOutcome) ? t("forYou.unknownOutcome") : rawOutcome;
          const rawMarketTitle = act.market_title || "";
          const marketTitleLabel = /^unknown\s*market$/i.test(rawMarketTitle.trim())
            ? t("forYou.unknownMarket")
            : rawMarketTitle;
          const isCopy = act.event_type === "copy_trade";
          const isTracker = act.event_type === "tracker_trade";
          const sizeUsd = act.size_usd ? Number(act.size_usd) : 0;
          const amountLabel = sizeUsd
            ? sizeUsd >= 1000
              ? `$${(sizeUsd / 1000).toFixed(1)}K`
              : `$${sizeUsd.toFixed(0)}`
            : null;
          const profileHref = act.source_wallet
            ? `/traders/@${act.source_wallet}`
            : `/profile/${act.source_username || "anonymous"}`;

          // Relative time, locale-aware via i18n keys.
          const diffMs = Date.now() - new Date(act.event_at).getTime();
          const diffMin = Math.floor(diffMs / 60000);
          const timeAgo =
            diffMin < 1
              ? t("forYou.timeNow")
              : diffMin < 60
                ? t("forYou.timeMinutes", { n: diffMin })
                : diffMin < 1440
                  ? t("forYou.timeHours", { n: Math.floor(diffMin / 60) })
                  : t("forYou.timeDays", { n: Math.floor(diffMin / 1440) });

          return (
            <div
              key={act.id}
              className="group bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-3 hover:border-gray-300 dark:hover:border-[#25252B] transition-colors"
            >
              {/* Row 1: User avatar + name + badge + time */}
              <div className="flex items-center gap-2.5 mb-2.5">
                <Link href={profileHref} onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                  {avatar ? (
                    <CdnImage src={avatar} alt="" width={40} height={40} className="w-10 h-10 rounded-[5px] object-cover ring-1 ring-gray-200 dark:ring-white/10" />
                  ) : (
                    <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] ring-1 ring-gray-200 dark:ring-white/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-400">{String(displayName)[0]?.toUpperCase()}</span>
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Link
                      href={profileHref}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm font-inter-tight font-bold text-gray-900 dark:text-white truncate hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      {displayName}
                    </Link>
                    {isCopy && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded-[5px] bg-violet-100 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400">
                        {t("forYou.frenBadgeCopy")}
                      </span>
                    )}
                    {isTracker && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded-[5px] bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400">
                        {t("forYou.frenBadgeTracked")}
                      </span>
                    )}
                    {!isCopy && !isTracker && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded-[5px] bg-gray-100 dark:bg-gray-500/10 text-gray-500 dark:text-gray-400">
                        {t("forYou.frenBadgeFren")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {isBuy ? (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                        <ArrowUpRight className="w-3 h-3" /> {t("forYou.bought", { outcome: outcomeLabel })}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-red-500 dark:text-red-400">
                        <ArrowDownRight className="w-3 h-3" /> {t("forYou.sold", { outcome: outcomeLabel })}
                      </span>
                    )}
                    {amountLabel && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-satoshi">{amountLabel}</span>
                    )}
                    {act.price != null && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-satoshi">@ {formatCents(Number(act.price), 2)}</span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-satoshi flex-shrink-0 self-start mt-0.5">{timeAgo}</span>
              </div>

              {/* Row 2: Market card with image */}
              {act.market_title && (
                <Link
                  href={sourceUrl(`/event/${act.market_slug}`, source)}
                  className="flex items-center gap-2.5 p-2 -mx-0.5 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] hover:bg-gray-100 dark:hover:bg-[#0f0f12]/80 transition-colors"
                >
                  {act.market_image && (
                    <CdnImage src={act.market_image} alt="" width={40} height={40} className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0" />
                  )}
                  <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-snug font-inter-tight">
                    {marketTitleLabel}
                  </p>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
